import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Users, LogOut, Plus, ShoppingCart, X, Minus, User, Bell, CheckCircle, Menu } from 'lucide-react';
import axios from 'axios';

import { useAuth, API, getImageUrl } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '../components/ui/sheet';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import CreateOrderModal from '../components/waiter/CreateOrderModal';
import AuthorizationModal from '../components/AuthorizationModal';
import { getErrorMessage } from '../utils/errorHandler';
import { formatAmount } from '../utils/format';
import { calcTotals, itbisLabel } from '../utils/tax';
const WaiterDashboard = () => {
  const { user, logout, getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [tables, setTables] = useState([]);
  const [selectedTable, setSelectedTable] = useState(null);
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [showEditOrderModal, setShowEditOrderModal] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [productToDelete, setProductToDelete] = useState(null);
  const [currentOrder, setCurrentOrder] = useState(null);
  const [products, setProducts] = useState([]);
  const [cart, setCart] = useState([]);
  const [productSearch, setProductSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [tenantInfo, setTenantInfo] = useState(null);
  const [tableCustomers, setTableCustomers] = useState({});
  
  // Estados para notificaciones de órdenes listas
  const [myOrders, setMyOrders] = useState([]);
  const [readyOrders, setReadyOrders] = useState([]);
  const [showReadyNotification, setShowReadyNotification] = useState(false);
  const audioRef = useRef(null);
  const previousOrdersRef = useRef([]);
  const soundIntervalRef = useRef(null);
  const isInitialLoadRef = useRef(true);

  useEffect(() => {
    loadTables();
    loadProducts();
    loadMyOrders();

    // Config del tenant: respeta el toggle "Cálculo de Impuestos" en la vista previa
    axios.get(`${API}/tenants/my-tenant`, { headers: getAuthHeaders() })
      .then((res) => setTenantInfo(res.data))
      .catch(() => {});
    
    const tablesInterval = setInterval(loadTables, 10000); // Refresh tables every 10 seconds
    const ordersInterval = setInterval(loadMyOrders, 5000); // Check for ready orders every 5 seconds
    
    // Marcar como carga inicial completada
    setTimeout(() => {
      isInitialLoadRef.current = false;
    }, 100);
    
    return () => {
      clearInterval(tablesInterval);
      clearInterval(ordersInterval);
    };
  }, []);

  const loadTables = async () => {
    try {
      const response = await axios.get(`${API}/tables`, {
        headers: getAuthHeaders()
      });
      setTables(response.data);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Error al cargar mesas'));
    }
  };

  const loadProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`, {
        headers: getAuthHeaders()
      });
      setProducts(Array.isArray(response.data) ? response.data.filter(p => p.is_available) : []);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Error al cargar productos'));
    }
  };

  const loadMyOrders = async () => {
    try {
      // Obtener la cola activa (pending, in_preparation, ready) con el mismo filtro
      // y el mismo limite que caja, cocina y delivery: los tres comparten clave de
      // caché en el backend, así que mesero y cajero ven exactamente el mismo snapshot.
      const response = await axios.get(`${API}/orders?status=pending&status=in_preparation&status=ready&limit=200`, {
        headers: getAuthHeaders()
      });
      
      console.info('=== DEBUG: loadMyOrders ===');
      console.info('Total orders (pending/in_preparation/ready):', response.data.length);
      console.info('Current user:', user);
      
      // Filtrar solo órdenes de mesa (dine_in) que fueron creadas por este mesero
      const ordersList = Array.isArray(response.data) ? response.data : [];

      // Mapa mesa -> nombre del cliente, construido de TODAS las ordenes abiertas
      // dine_in (no solo las de este mesero) para mostrarlo en la grilla de mesas.
      const customerMap = {};
      ordersList.forEach((order) => {
        if (order.order_type === 'dine_in' && order.table_id && order.customer_name) {
          customerMap[order.table_id] = order.customer_name;
        }
      });
      setTableCustomers(customerMap);

      const waiterDineInOrders = ordersList.filter(
        order => {
          const isDineIn = order.order_type === 'dine_in';
          const isMyOrder = order.waiter_id === user?.id;
          console.info(`Order #${order.order_number}: status=${order.status}, dine_in=${isDineIn}, waiter_id=${order.waiter_id}, user.id=${user?.id}, match=${isMyOrder}`);
          return isDineIn && isMyOrder;
        }
      );
      
      console.info('Filtered waiter dine-in orders:', waiterDineInOrders.length);
      setMyOrders(waiterDineInOrders);
    } catch (error) {
      // Silenciar errores para no molestar al usuario
      console.error('Error loading orders:', error);
    }
  };

  // Efecto para crear el sonido de notificación
  useEffect(() => {
    const createBeepSound = () => {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.frequency.value = 600; // Frecuencia del beep
      oscillator.type = 'sine';
      
      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);
      
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.5);
    };
    
    audioRef.current = createBeepSound;
  }, []);

  // Detectar items listos y items nuevos agregados
  useEffect(() => {
    // Si es la primera carga, solo guardar las órdenes sin notificar
    if (isInitialLoadRef.current) {
      previousOrdersRef.current = myOrders;
      return;
    }
    
    // Crear mapa de órdenes anteriores con sus items
    const previousOrdersMap = new Map(
      previousOrdersRef.current.map(o => [
        o.id, 
        { 
          status: o.status, 
          items: o.items || [],
          itemsMap: new Map((o.items || []).map(item => [`${item.product_id}_${item.variant_id || ''}`, item]))
        }
      ])
    );
    
    const notifications = [];
    
    // Revisar cada orden actual
    myOrders.forEach(order => {
      const previousOrder = previousOrdersMap.get(order.id);
      const currentItems = order.items || [];
      
      // CASO 1: Detectar items individuales que pasaron a "ready"
      if (previousOrder) {
        currentItems.forEach(currentItem => {
          const itemKey = `${currentItem.product_id}_${currentItem.variant_id || ''}`;
          const previousItem = previousOrder.itemsMap.get(itemKey);
          
          // Si el item cambió de otro status a "ready"
          if (previousItem && previousItem.status !== 'ready' && currentItem.status === 'ready') {
            console.info(`[NOTIFY] Item listo: ${currentItem.product_name} en Mesa ${order.table_id}`);
            notifications.push({
              type: 'item_ready',
              order: order,
              item: currentItem,
              message: `${currentItem.product_name} está listo`,
              tableNumber: order.table_id
            });
          }
        });
        
        // CASO 2: Detectar items nuevos agregados (que no existían antes)
        const previousItemKeys = new Set(previousOrder.itemsMap.keys());
        currentItems.forEach(currentItem => {
          const itemKey = `${currentItem.product_id}_${currentItem.variant_id || ''}`;
          
          // Si es un item nuevo que no existía antes
          if (!previousItemKeys.has(itemKey)) {
            console.info(`[NOTIFY] Item agregado: ${currentItem.product_name} en Mesa ${order.table_id}`);
            notifications.push({
              type: 'item_added',
              order: order,
              item: currentItem,
              message: `Se agregó ${currentItem.quantity}x ${currentItem.product_name}`,
              tableNumber: order.table_id
            });
          }
        });
      }
    });
    
    // Si hay notificaciones, mostrarlas
    if (notifications.length > 0) {
      console.info(`[NOTIFY] Total notificaciones: ${notifications.length}`);
      
      // Agrupar por mesa
      const notificationsByTable = notifications.reduce((acc, notif) => {
        const tableId = notif.tableNumber;
        if (!acc[tableId]) acc[tableId] = [];
        acc[tableId].push(notif);
        return acc;
      }, {});
      
      // Mostrar notificaciones agrupadas
      Object.entries(notificationsByTable).forEach(([tableId, notifs]) => {
        const messages = notifs.map(n => n.message).join(' • ');
        toast.success(`Mesa ${tableId}: ${messages}`, {
          duration: 8000,
          icon: notifs[0].type === 'item_ready' ? '✅' : '➕'
        });
      });
      
      playNotificationSound();
      setReadyOrders(notifications.map(n => n.order));
      setShowReadyNotification(true);
    }
    
    // Actualizar referencia de órdenes anteriores
    previousOrdersRef.current = myOrders;
  }, [myOrders]);

  const playNotificationSound = () => {
    // Limpiar cualquier intervalo anterior
    if (soundIntervalRef.current) {
      clearInterval(soundIntervalRef.current);
    }
    
    // Reproducir el sonido 3 veces con intervalo de 1 segundo
    let count = 0;
    const playSound = () => {
      if (audioRef.current && count < 3) {
        audioRef.current();
        count++;
      } else {
        clearInterval(soundIntervalRef.current);
      }
    };
    
    playSound(); // Primera reproducción inmediata
    soundIntervalRef.current = setInterval(playSound, 1000);
  };

  const handleAcknowledgeReadyOrders = () => {
    setShowReadyNotification(false);
    setReadyOrders([]);
    
    // Detener el sonido
    if (soundIntervalRef.current) {
      clearInterval(soundIntervalRef.current);
    }
  };

  const handleTableClick = async (table) => {
    if (table.status === 'free') {
      setSelectedTable(table);
      setShowOrderModal(true);
    } else if (table.status === 'occupied' && table.current_order_id) {
      // Open order for editing
      await handleOpenOrder(table);
    }
  };

  const handleOpenOrder = async (table) => {
    try {
      setLoading(true);
      // Obtener la orden actual de la mesa
      const response = await axios.get(
        `${API}/orders/${table.current_order_id}`,
        { headers: getAuthHeaders() }
      );
      
      const order = response.data;
      
      // Verificar que la orden no esté entregada o cancelada
      if (order.status === 'delivered' || order.status === 'cancelled') {
        toast.error('Esta mesa tiene una orden finalizada. Liberando mesa...');
        // Liberar la mesa automáticamente
        await axios.put(
          `${API}/tables/${table.id}/status`,
          '"free"',
          { 
            headers: {
              ...getAuthHeaders(),
              'Content-Type': 'application/json'
            }
          }
        );
        // Recargar las mesas
        await loadTables();
        return;
      }
      
      setCurrentOrder(order);
      setCart(order.items.map(item => ({
        ...item,
        image_url: products.find(p => p.id === item.product_id)?.image_url
      })));
      setSelectedTable(table);
      setShowEditOrderModal(true);
    } catch (error) {
      // Si la orden no existe, liberar la mesa
      if (error.response?.status === 404) {
        toast.error('Orden no encontrada. Liberando mesa...');
        await axios.put(
          `${API}/tables/${table.id}/status`,
          '"free"',
          { 
            headers: {
              ...getAuthHeaders(),
              'Content-Type': 'application/json'
            }
          }
        );
        await loadTables();
      } else {
        toast.error(getErrorMessage(error, 'Error al cargar la orden'));
      }
    } finally {
      setLoading(false);
    }
  };

  const addToCart = (product) => {
    const existingItem = cart.find(item => item.product_id === product.id);
    if (existingItem) {
      setCart(cart.map(item =>
        item.product_id === product.id
          ? { ...item, quantity: item.quantity + 1, subtotal: (item.quantity + 1) * item.unit_price }
          : item
      ));
    } else {
      setCart([...cart, {
        product_id: product.id,
        product_name: product.name,
        quantity: 1,
        unit_price: product.price,
        subtotal: product.price,
        image_url: product.image_url
      }]);
    }
  };

  const requestRemoveFromCart = (productId) => {
    // Check if user is admin or super_admin
    if (user?.role === 'admin' || user?.role === 'super_admin') {
      // Admin can remove directly
      removeFromCart(productId);
    } else {
      // Request authorization
      setProductToDelete(productId);
      setShowAuthModal(true);
    }
  };

  const removeFromCart = (productId) => {
    setCart(cart.filter(item => item.product_id !== productId));
  };

  const handleAuthorized = () => {
    if (productToDelete) {
      removeFromCart(productToDelete);
      setProductToDelete(null);
    }
  };

  const updateQuantity = (productId, newQuantity) => {
    if (newQuantity < 1) {
      requestRemoveFromCart(productId);
      return;
    }
    setCart(cart.map(item =>
      item.product_id === productId
        ? { ...item, quantity: newQuantity, subtotal: newQuantity * item.unit_price }
        : item
    ));
  };

  const calculateTotal = () => {
    const subtotal = cart.reduce((sum, item) => sum + item.subtotal, 0);
    return calcTotals(subtotal, tenantInfo);
  };

  const handleUpdateOrder = async () => {
    // Si el carrito está vacío, cancelar la orden y liberar la mesa
    if (cart.length === 0) {
      try {
        setLoading(true);
        
        // Cancelar la orden (el backend automáticamente libera la mesa)
        await axios.put(
          `${API}/orders/${currentOrder.id}/status?status=cancelled`,
          {},
          { headers: getAuthHeaders() }
        );
        
        toast.success('Orden cancelada y mesa liberada');
        setShowEditOrderModal(false);
        setCurrentOrder(null);
        setCart([]);
        setSelectedTable(null);
        loadTables();
        return;
      } catch (error) {
        toast.error(getErrorMessage(error, 'Error al cancelar la orden'));
        setLoading(false);
        return;
      }
    }

    // Si hay productos, actualizar la orden normalmente
    if (!currentOrder) {
      toast.error('No hay orden seleccionada');
      return;
    }

    setLoading(true);
    try {
      // Enviar items simplificados (product_id, variant_id, quantity, notes) para que el backend los enriquezca
      const simpleItems = cart.map(item => ({
        product_id: item.product_id,
        variant_id: item.variant_id || null,
        quantity: item.quantity,
        notes: item.notes || ''
      }));

      const updateResponse = await axios.put(
        `${API}/orders/${currentOrder.id}/items`,
        simpleItems,
        { headers: getAuthHeaders() }
      );

      // Check if order was cancelled due to empty cart
      if (updateResponse.data.order_cancelled) {
        toast.success('¡Mesa liberada! (Se eliminaron todos los productos)');
        setShowEditOrderModal(false);
        setCurrentOrder(null);
        setCart([]);
        loadTables();
        return;
      }

      // Generate new comandas only if order still exists
      try {
        await axios.post(
          `${API}/orders/${currentOrder.id}/comandas`,
          {},
          { headers: getAuthHeaders() }
        );
        toast.success('¡Orden actualizada y comandas enviadas!');
      } catch (error) {
        // Si las comandas fallan, igual avisamos que la orden se actualizó
        console.warn('No se pudieron reimprimir las comandas:', error);
        toast.success('¡Orden actualizada!');
      }

      setShowEditOrderModal(false);
      setCurrentOrder(null);
      setCart([]);
      loadTables();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Error al actualizar orden'));
    }
    setLoading(false);
  };

  const handleOrderCreated = () => {
    setShowOrderModal(false);
    setSelectedTable(null);
    loadTables();
  };

  const getTableStatusClass = (status) => {
    switch (status) {
      case 'free':
        return 'bg-green-50 border-green-300 hover:bg-green-100';
      case 'occupied':
        return 'bg-orange-50 border-orange-300 hover:bg-orange-100';
      case 'reserved':
        return 'bg-blue-50 border-blue-300 hover:bg-blue-100';
      default:
        return '';
    }
  };

  const getTableStatusLabel = (status) => {
    switch (status) {
      case 'free':
        return 'Libre';
      case 'occupied':
        return 'Ocupada';
      case 'reserved':
        return 'Reservada';
      default:
        return status;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-amber-50 to-red-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-xl border-b border-orange-100 sticky top-0 z-50 shadow-sm">
        <div className="max-w-full mx-auto px-3 sm:px-4 lg:px-8 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 sm:gap-3">
              {/* Mobile Menu */}
              <Sheet>
                <SheetTrigger asChild>
                  <Button variant="outline" size="sm" className="md:hidden p-2 border-orange-200">
                    <Menu className="w-5 h-5" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="left" className="w-[280px] sm:w-[350px]">
                  <SheetHeader>
                    <SheetTitle>Menú Mesero</SheetTitle>
                  </SheetHeader>
                  <div className="mt-6 space-y-4">
                    <div className="pb-4 border-b">
                      <p className="text-sm text-gray-500">Usuario</p>
                      <p className="font-semibold">{user?.name}</p>
                      <p className="text-xs text-gray-400">{user?.email}</p>
                    </div>
                    
                    <div className="bg-orange-50 p-3 rounded-lg border border-orange-200">
                      <p className="text-xs text-gray-600 mb-1">Órdenes Listas</p>
                      <p className="text-2xl font-bold text-orange-600">{readyOrders.length}</p>
                    </div>
                    
                    <Button
                      variant="outline"
                      onClick={() => navigate('/profile')}
                      className="w-full justify-start"
                    >
                      <User className="w-4 h-4 mr-2" />
                      Mi Perfil
                    </Button>
                    
                    <Button
                      variant="destructive"
                      onClick={logout}
                      className="w-full justify-start"
                    >
                      <LogOut className="w-4 h-4 mr-2" />
                      Cerrar Sesión
                    </Button>
                  </div>
                </SheetContent>
              </Sheet>
              
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-br from-orange-500 to-red-500 rounded-xl flex items-center justify-center flex-shrink-0">
                <Users className="w-4 h-4 sm:w-6 sm:h-6 text-white" />
              </div>
              <div>
                <h1 className="text-base sm:text-xl lg:text-2xl font-bold text-gray-800">Mesero</h1>
                <p className="text-xs sm:text-sm text-gray-600 hidden sm:block">{user?.name}</p>
              </div>
              
              {/* Ready orders badge - visible on all screens */}
              {readyOrders.length > 0 && (
                <div className="ml-2 flex items-center gap-2 bg-green-100 text-green-700 px-2 sm:px-3 py-1 rounded-full border border-green-300 animate-pulse">
                  <Bell className="w-3 h-3 sm:w-4 sm:h-4" />
                  <span className="text-xs sm:text-sm font-bold">{readyOrders.length}</span>
                  <span className="hidden sm:inline text-xs">listas</span>
                </div>
              )}
            </div>
            
            {/* User Menu - Hidden on mobile */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="hidden md:flex border-orange-200 hover:bg-orange-50 rounded-xl">
                  <User className="w-4 h-4 sm:mr-2" />
                  <span className="hidden sm:inline">{user?.name}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>Mi Cuenta</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate('/profile')}>
                  <User className="w-4 h-4 mr-2" />
                  Perfil
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={logout}>
                  <LogOut className="w-4 h-4 mr-2" />
                  Salir
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      <main className="max-w-full mx-auto px-3 sm:px-4 lg:px-8 py-4 sm:py-8">
        <div className="mb-4 sm:mb-6">
          <h2 className="text-lg sm:text-2xl font-bold text-gray-800 mb-2">Gestión de Mesas</h2>
          <p className="text-xs sm:text-sm text-gray-600">
            <span className="text-green-600 font-semibold">Libre</span> para crear | 
            <span className="text-orange-600 font-semibold"> Ocupada</span> para editar
          </p>
        </div>

        {/* Tables Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3 sm:gap-4">
          {tables.map((table) => (
            <Card
              key={table.id}
              onClick={() => handleTableClick(table)}
              className={`p-6 cursor-pointer transition-all duration-200 border-2 ${getTableStatusClass(table.status)} hover-lift`}
            >
              <div className="text-center">
                <div className="text-3xl font-bold text-gray-800 mb-2">
                  {table.number}
                </div>
                {table.status === 'occupied' && (
                  <div
                    className="mb-2 text-xs font-semibold text-gray-700 truncate px-1"
                    title={tableCustomers[table.id] || 'Cliente sin nombre'}
                  >
                    <User className="w-3 h-3 inline-block mr-1 text-orange-500 -mt-0.5" />
                    {tableCustomers[table.id] || 'Sin nombre'}
                  </div>
                )}
                <div className="text-sm text-gray-600 mb-2">
                  Capacidad: {table.capacity}
                </div>
                <div className={`text-xs font-semibold px-2 py-1 rounded-full ${
                  table.status === 'free' ? 'bg-green-200 text-green-800' :
                  table.status === 'occupied' ? 'bg-orange-200 text-orange-800' :
                  'bg-blue-200 text-blue-800'
                }`}>
                  {getTableStatusLabel(table.status)}
                </div>
                {table.status === 'occupied' && (
                  <div className="mt-2 text-xs text-gray-500">
                    Click para editar
                  </div>
                )}
              </div>
            </Card>
          ))}
        </div>

        {tables.length === 0 && (
          <div className="text-center py-12">
            <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No hay mesas configuradas</p>
          </div>
        )}
      </main>

      {/* Create Order Modal */}
      {showOrderModal && selectedTable && (
        <CreateOrderModal
          tenant={tenantInfo}
          isOpen={showOrderModal}
          onClose={() => {
            setShowOrderModal(false);
            setSelectedTable(null);
          }}
          tableId={selectedTable.id}
          tableNumber={selectedTable.number}
          onOrderCreated={handleOrderCreated}
        />
      )}

      {/* Edit Order Modal */}
      <Dialog open={showEditOrderModal} onOpenChange={setShowEditOrderModal}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl">
              Editar Orden - Mesa {selectedTable?.number}
            </DialogTitle>
          </DialogHeader>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Products Section */}
            <div className="space-y-4">
              <div>
                <Label>Buscar Productos para Agregar</Label>
                <Input
                  placeholder="Buscar por nombre..."
                  value={productSearch}
                  onChange={(e) => setProductSearch(e.target.value)}
                  className="rounded-xl"
                />
              </div>

              <div className="space-y-2 max-h-96 overflow-y-auto">
                {products
                  .filter(p => p.name.toLowerCase().includes(productSearch.toLowerCase()))
                  .map((product) => (
                    <Card
                      key={product.id}
                      className="p-3 hover:bg-orange-50 cursor-pointer transition-colors"
                      onClick={() => addToCart(product)}
                    >
                      <div className="flex items-center gap-3">
                        {product.image_url && (
                          <img
                            src={getImageUrl(product.image_url)}
                            alt={product.name}
                            className="w-16 h-16 object-cover rounded-lg"
                          />
                        )}
                        <div className="flex-1">
                          <h4 className="font-semibold text-gray-800">{product.name}</h4>
                          <p className="text-sm text-gray-600">{product.description}</p>
                          <p className="text-lg font-bold text-orange-600">${formatAmount(product.price)}</p>
                        </div>
                        <Plus className="w-5 h-5 text-green-500" />
                      </div>
                    </Card>
                  ))}
              </div>
            </div>

            {/* Cart Section */}
            <div className="space-y-4">
              <div className="bg-blue-50 p-4 rounded-xl">
                <div className="flex items-center gap-2 mb-3">
                  <ShoppingCart className="w-5 h-5 text-blue-600" />
                  <h3 className="font-bold text-gray-800">Productos en la Orden ({cart.length})</h3>
                </div>

                {cart.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">No hay productos en la orden</p>
                ) : (
                  <div className="space-y-3">
                    {cart.map((item) => (
                      <Card key={item.product_id} className="p-3">
                        <div className="flex items-center gap-3">
                          {item.image_url && (
                            <img
                              src={getImageUrl(item.image_url)}
                              alt={item.product_name}
                              className="w-12 h-12 object-cover rounded-lg"
                            />
                          )}
                          <div className="flex-1">
                            <h4 className="font-semibold text-sm">{item.product_name}</h4>
                            <p className="text-xs text-gray-600">${formatAmount(item.unit_price)} c/u</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => updateQuantity(item.product_id, item.quantity - 1)}
                              className="h-8 w-8 p-0 rounded-lg"
                            >
                              <Minus className="w-3 h-3" />
                            </Button>
                            <span className="font-bold w-8 text-center">{item.quantity}</span>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => updateQuantity(item.product_id, item.quantity + 1)}
                              className="h-8 w-8 p-0 rounded-lg"
                            >
                              <Plus className="w-3 h-3" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => requestRemoveFromCart(item.product_id)}
                              className="h-8 w-8 p-0 rounded-lg text-red-600 hover:bg-red-50"
                            >
                              <X className="w-3 h-3" />
                            </Button>
                          </div>
                        </div>
                        <div className="text-right mt-2">
                          <span className="font-bold text-orange-600">${formatAmount(item.subtotal)}</span>
                        </div>
                      </Card>
                    ))}
                  </div>
                )}
              </div>

              {/* Totals */}
              {cart.length > 0 && (
                <div className="bg-white p-4 rounded-xl border-2 border-blue-200">
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Subtotal:</span>
                      <span className="font-semibold">${formatAmount(calculateTotal().subtotal)}</span>
                    </div>
                    {calculateTotal().tax > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">{itbisLabel()}:</span>
                        <span className="font-semibold">${formatAmount(calculateTotal().tax)}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-xl font-bold pt-2 border-t">
                      <span>Total:</span>
                      <span className="text-orange-600">${formatAmount(calculateTotal().total)}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button
              onClick={() => setShowEditOrderModal(false)}
              variant="outline"
              className="rounded-xl"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleUpdateOrder}
              disabled={loading}
              className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 rounded-xl"
            >
              {loading ? 'Actualizando...' : cart.length === 0 ? 'Cancelar Orden y Liberar Mesa' : 'Actualizar y Enviar Comandas'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Authorization Modal */}
      <AuthorizationModal
        isOpen={showAuthModal}
        onClose={() => {
          setShowAuthModal(false);
          setProductToDelete(null);
        }}
        onAuthorized={handleAuthorized}
        action="eliminar productos de la orden"
      />

      {/* Ready Orders Notification */}
      <Dialog 
        open={showReadyNotification} 
        onOpenChange={(open) => {
          if (!open) handleAcknowledgeReadyOrders();
        }}
      >
        <DialogContent 
          className="sm:max-w-md bg-gradient-to-br from-green-500 to-emerald-600 text-white border-0"
          onInteractOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-white flex items-center gap-3">
              <CheckCircle className="w-8 h-8 animate-bounce" />
              ¡Orden{readyOrders.length > 1 ? 'es' : ''} Lista{readyOrders.length > 1 ? 's' : ''}!
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 space-y-3">
              {readyOrders.map((order) => (
                <div key={order.id} className="bg-white/30 rounded-lg p-3">
                  <p className="text-lg font-bold">Orden #{order.order_number}</p>
                  {order.customer_name && (
                    <p className="text-sm font-semibold opacity-95">
                      👤 {order.customer_name}
                    </p>
                  )}
                  <p className="text-sm opacity-90">
                    🍽️ Mesa {order.table_id ? tables.find(t => t.id === order.table_id)?.number : ''}
                  </p>
                  <div className="mt-2 space-y-1">
                    {order.items.slice(0, 3).map((item, idx) => (
                      <p key={idx} className="text-sm font-medium">
                        • {item.quantity}x {item.product_name}
                      </p>
                    ))}
                    {order.items.length > 3 && (
                      <p className="text-sm font-semibold">
                        + {order.items.length - 3} producto(s) más
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className="bg-yellow-400/30 backdrop-blur-sm rounded-xl p-3">
              <p className="text-sm font-semibold text-center">
                🚨 Servir a la mesa ahora
              </p>
            </div>

            <Button
              onClick={handleAcknowledgeReadyOrders}
              className="w-full bg-white text-green-600 hover:bg-gray-100 font-bold text-lg py-6 rounded-xl shadow-lg"
            >
              ✓ Entendido
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WaiterDashboard;
